import Actions from 'actions'
import useRolePermissions from 'hooks/useRolePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getEnvironment } from 'utils/awsUtils'
import isEmpty from 'lodash/isEmpty'
import { trackEvent } from 'utils/analyticsUtils'
import { getMappedDeviceSkuKey } from './rcDeviceKeyUtils'

const PLACEHOLDER = '-'

export default ({ lead } = {}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { permissions } = useRolePermissions()

  const shippingStatusOptions = useMemo(
    () => [
      {
        label: PLACEHOLDER,
        value: PLACEHOLDER
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.shipmentRequired'),
        value: 'shipmentRequired'
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.processing'),
        value: 'processing'
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.shipped'),
        value: 'shipped'
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.delivered'),
        value: 'delivered'
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.notRequired'),
        value: 'notRequired'
      },
      {
        label: t('pages.rcDashboard.dialogs.shippingDetails.shippingStatuses.onHold'),
        value: 'onHold'
      }
    ],
    [t]
  )

  const { inventoryItems, shippingMethods, deviceSkuMap } = useSelector(
    state => state.rcDashboardReducer.shippingDetailsDialog
  )

  const shippingMethodOptions = useMemo(
    () => shippingMethods?.map(shippingMethod => shippingMethod.value),
    [shippingMethods]
  )

  const rcData = useMemo(() => JSON.parse(lead?.rcData || '{}'), [lead])
  const shippingDetails = useMemo(() => rcData.shippingDetails, [rcData])
  const shipbobIntegrationActive = useMemo(() => !!shippingDetails?.shipbobReferenceId, [shippingDetails])
  const shipbobWebUrl = useMemo(
    () =>
      getEnvironment() === 'production'
        ? 'https://web.shipbob.com/app/merchant/#'
        : 'https://webstage.shipbob.com/app/merchant/#',
    []
  )

  const [shippingMethod, setShippingMethod] = useState(null)
  const [shippingStatus, setShippingStatus] = useState(shippingStatusOptions[0])
  const [inventoryItemId, setInventoryItemId] = useState(null)
  const [trackingNumber, setTrackingNumber] = useState()

  useEffect(() => {
    setShippingStatus(
      shippingStatusOptions.find(option => option?.value === rcData?.shippingDetails?.status) || PLACEHOLDER
    )
    setShippingMethod(rcData?.shippingDetails?.shippingMethod)
    setInventoryItemId(inventoryItems?.find(item => item.value === rcData?.shippingDetails?.inventoryItemId))
    setTrackingNumber(rcData?.shippingDetails?.trackingNumber)
  }, [rcData, shippingStatusOptions, inventoryItems])

  useEffect(() => {
    if (!inventoryItems && permissions.rcDashboardAdminActions) {
      dispatch(Actions.fetchRcShippingConfiguration())
    }
  }, [inventoryItems, dispatch, permissions])

  useEffect(() => {
    const selectedInventoryItem = rcData?.shippingDetails?.inventoryItemId
    if (inventoryItems && selectedInventoryItem) {
      const selectedString = String(selectedInventoryItem)
      setInventoryItemId(inventoryItems.find(item => String(item.value) === selectedString))
      return
    }

    if (
      inventoryItems &&
      !selectedInventoryItem &&
      !shipbobIntegrationActive &&
      deviceSkuMap &&
      !isEmpty(deviceSkuMap)
    ) {
      const deviceKey = getMappedDeviceSkuKey({ deviceSkuMap, rcDeviceData: rcData })
      const mappedInventoryItemId = deviceKey ? deviceSkuMap[deviceKey] : null

      if (mappedInventoryItemId) {
        const mappedInventoryItem = inventoryItems.find(item => String(item.value) === String(mappedInventoryItemId))
        if (mappedInventoryItem) {
          setInventoryItemId(mappedInventoryItem)
          trackEvent('RC shipping - SKU auto-selected', {
            leadId: lead?.id,
            deviceType: rcData.deviceType,
            deviceModel: rcData.deviceModel,
            deviceConnector: rcData.deviceConnector,
            deviceKey,
            inventoryItemId: String(mappedInventoryItem.value),
            inventoryItemName: mappedInventoryItem.originalName
          })
        }
      }
    }
  }, [inventoryItems, rcData, shipbobIntegrationActive, deviceSkuMap, lead?.id])

  useEffect(() => {
    setShippingMethod(shippingMethodOptions?.[0])
  }, [shippingMethodOptions])

  return {
    shippingStatusOptions,
    shippingMethodOptions,
    shipbobIntegrationActive,
    shippingDetails,
    shipbobWebUrl,
    inventoryItems,
    values: {
      shippingMethod,
      setShippingMethod,
      shippingStatus,
      setShippingStatus,
      inventoryItemId,
      setInventoryItemId,
      trackingNumber,
      setTrackingNumber,
      trackingUrl: shippingDetails?.trackingUrl
    }
  }
}
